import React, { ReactElement, useContext } from 'react'

import Heading from '../../Common/Components/Heading'
import P from '../../Common/Components/P'
import Fieldset from '../../Common/Components/Fieldset'
import Checkbox from '../../Common/Components/Checkbox'
import TextInput from '../../Common/Components/TextInput'
import FormRow from '../../Common/Components/FormRow'

import { LanguageContext } from '../../Common/Contexts'

import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { ServiceLvlSettings as ServiceLvlSettingsLanguage } from '../../../../i18n'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'

/**
 * Component properties type.
 */
type Props = {
    callsAnsWithin: number | undefined
    setCallsAnsWithin: (newValue: number | undefined) => void
    isSrvLvlIgnQuickAbndCallsWithinEnabled: boolean
    setSrvLvlIgnQuickAbndCallsWithinEnabled: (checked: boolean) => void
    srvLvlIgnQuickAbndCallsWithin: number | undefined
    setSrvLvlIgnQuickAbndCallsWithin: (newValue: number | undefined) => void
    isSrvLvlIgnOvrflwdCallsWithinEnabled: boolean
    setSrvLvlIgnOvrflwdCallsWithinEnabled: (checked: boolean) => void
    srvLvlIgnOvrflwdCallsWithin: number | undefined
    setSrvLvlIgnOvrflwdCallsWithin: (newValue: number | undefined) => void
}

/**
 * Service level settings section of SLA/KPI tab in report configuration modal window.
 *
 * @param callsAnsWithin Report property.
 * @param setCallsAnsWithin Setter for corresponding report property.
 * @param isSrvLvlIgnQuickAbndCallsWithinEnabled Whether the input for corresponding property is enabled.
 * @param setSrvLvlIgnQuickAbndCallsWithinEnabled Setter to enable corresponding property input.
 * @param srvLvlIgnQuickAbndCallsWithin Report property.
 * @param setSrvLvlIgnQuickAbndCallsWithin Setter for corresponding report property.
 * @param isSrvLvlIgnOvrflwdCallsWithinEnabled Whether the input for corresponding property is enabled.
 * @param setSrvLvlIgnOvrflwdCallsWithinEnabled Setter to enable corresponding property input.
 * @param srvLvlIgnOvrflwdCallsWithin Report property.
 * @param setSrvLvlIgnOvrflwdCallsWithin Setter for corresponding report property.
 */
const ServiceLvlSettings: React.FC<Props> = ({
    callsAnsWithin,
    setCallsAnsWithin,
    isSrvLvlIgnQuickAbndCallsWithinEnabled,
    setSrvLvlIgnQuickAbndCallsWithinEnabled,
    srvLvlIgnQuickAbndCallsWithin,
    setSrvLvlIgnQuickAbndCallsWithin,
    isSrvLvlIgnOvrflwdCallsWithinEnabled,
    setSrvLvlIgnOvrflwdCallsWithinEnabled,
    srvLvlIgnOvrflwdCallsWithin,
    setSrvLvlIgnOvrflwdCallsWithin
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('ServiceLvlSettings')

    return (
        <Fieldset className={'service-level-settings'}>
            <Heading level={HeadingLevel.ONE}>
                {language.get(ServiceLvlSettingsLanguage.ServiceLvlSettings_SectionHeader)}
            </Heading>
            <P>{language.get(ServiceLvlSettingsLanguage.ServiceLvlSettings_SectionPara)}</P>
            <FormRow isInline className={'calls-ans-within'}>
                <TextInput
                    value={callsAnsWithin}
                    onChange={(value: string): void => setCallsAnsWithin(parseInt(value, 10) || undefined)}
                    labelLeft={language.get(ServiceLvlSettingsLanguage.CallsansweredwithinLabel)}
                    placeholder={language.get(ServiceLvlSettingsLanguage.CallsansweredwithinPlaceholder)}
                    adornmentEnd={language.get(ServiceLvlSettingsLanguage.CallsansweredwithinUnit)}
                />
            </FormRow>
            <FormRow isInline className={'ignore-quickly-abandoned-calls'}>
                <Checkbox
                    labelLeft={language.get(
                        ServiceLvlSettingsLanguage.ServiceLvlSettings_IgnorequickabndcallswithinEnabledHiddenlabel
                    )}
                    isLabelHidden
                    onChange={setSrvLvlIgnQuickAbndCallsWithinEnabled}
                    isChecked={isSrvLvlIgnQuickAbndCallsWithinEnabled}
                />
                <TextInput
                    value={srvLvlIgnQuickAbndCallsWithin}
                    onChange={(value: string): void =>
                        setSrvLvlIgnQuickAbndCallsWithin(parseInt(value, 10) || undefined)
                    }
                    labelLeft={language.get(
                        ServiceLvlSettingsLanguage.ServiceLvlSettings_IgnorequickabndcallswithinLabel
                    )}
                    placeholder={language.get(
                        ServiceLvlSettingsLanguage.ServiceLvlSettings_IgnorequickabndcallswithinPlaceholder
                    )}
                    disabled={!isSrvLvlIgnQuickAbndCallsWithinEnabled}
                    adornmentEnd={language.get(
                        ServiceLvlSettingsLanguage.ServiceLvlSettings_IgnorequickabndcallswithinUnit
                    )}
                />
            </FormRow>
            <FormRow isInline className={'ignore-overflowed-calls'}>
                <Checkbox
                    labelLeft={language.get(
                        ServiceLvlSettingsLanguage.ServiceLvlSettings_IgnoreovrflwdcallswithinEnabledHiddenlabel
                    )}
                    isLabelHidden
                    onChange={setSrvLvlIgnOvrflwdCallsWithinEnabled}
                    isChecked={isSrvLvlIgnOvrflwdCallsWithinEnabled}
                />
                <TextInput
                    value={srvLvlIgnOvrflwdCallsWithin}
                    onChange={(value: string): void => setSrvLvlIgnOvrflwdCallsWithin(parseInt(value, 10) || undefined)}
                    labelLeft={language.get(
                        ServiceLvlSettingsLanguage.ServiceLvlSettings_IgnoreovrflwdcallswithinLabel
                    )}
                    placeholder={language.get(
                        ServiceLvlSettingsLanguage.ServiceLvlSettings_IgnoreovrflwdcallswithinPlaceholder
                    )}
                    disabled={!isSrvLvlIgnOvrflwdCallsWithinEnabled}
                    adornmentEnd={language.get(
                        ServiceLvlSettingsLanguage.ServiceLvlSettings_IgnoreovrflwdcallswithinUnit
                    )}
                />
            </FormRow>
        </Fieldset>
    )
}

export default React.memo(ServiceLvlSettings)
